import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '../supabase/client';
import { Database } from '@/database.types';
import { useAuth } from '../context/AuthContext';

export interface DiscussionQueryParams {
  categoryId?: string
  searchQuery?: string
  filter?: 'all' | 'pinned' | 'solved' | 'unsolved' | 'my-posts' | 'my-replies'
  sort?: 'recent' | 'popular' | 'views' | 'replies' | 'likes'
  page?: number
  limit?: number
}

export type User = Database['public']['Tables']['profiles']['Row']
export type Category = Database['public']['Tables']['forums_categories']['Row']
export type Discussion = Database['public']['Tables']['forums_discussions']['Row']
export type Comment = Database['public']['Tables']['forums_comments']['Row']
export type Like = Database['public']['Tables']['forums_likes']['Row']
export type Bookmark = Database['public']['Tables']['forums_bookmarks']['Row']

// Extended types with relationships
export interface DiscussionWithRelations {
  id: string;
  title: string;
  preview: string;
  category_id: string;
  user: User;
  created_at: string;
  last_activity_at: string;
  views: number;
  is_sticky: boolean;
  _count?: {
    comments: number;
    likes: number;
  };
}

export interface DiscussionDetail extends DiscussionWithRelations {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  category_id: string;
  views: number;
  is_sticky: boolean;
  is_liked: boolean;
  is_bookmarked: boolean;
  is_solved: boolean; // Added this field
  _count?: {
    likes: number;
    comments: number;
  };
  comments?: Array<{
    id: string;
    content: string;
    created_at: string;
    user_id: string;
    is_liked: boolean;
    _count?: {
      likes: number;
    };
    user: {
      id: string;
      username: string;
      experience_level: string;
    };
  }>;
}

export interface CommentWithRelations extends Comment {
  user: Pick<User, 'id' | 'username' | 'experience_level' | 'avatar_url'>
  _count?: {
    likes: number
  }
}

export type ViewType = 'main' | 'discussion-detail' | 'guidelines' | 'username-selection';

// Get current user session
export const useSession = () => {
  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      return data.session;
    }
  });
};

// Get forum categories
export const useCategories = () => {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('forums_categories')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data || [];
    }
  });
};

// Get discussions with optional filtering
export const useDiscussions = (params: DiscussionQueryParams = {}) => {
  const { categoryId, searchQuery, filter, sort, page = 1, limit = 10 } = params;
  const queryClient = useQueryClient();
  const session = queryClient.getQueryData<any>(['session']);
  const userId = session?.user?.id;

  return useQuery({
    queryKey: ['discussions', categoryId, searchQuery, filter, sort, page, limit],
    queryFn: async (): Promise<{ discussions: DiscussionWithRelations[], totalCount: number }> => {
      // Start building the query
      let query = supabase
        .from('forums_discussions')
        .select(`
          *,
          user:user_id (id, username, experience_level, avatar_url),
          category:category_id (id, slug, name, icon),
          comments:forums_comments (id),
          likes:forums_likes (id)
        `, { count: 'exact' });

      // Apply category filter if specified
      if (categoryId && categoryId !== 'all') {
        query = query.eq('category_id', categoryId);
      }

      // Apply search filter if provided
      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%, content.ilike.%${searchQuery}%`);
      }

      // Apply additional filters
      if (filter) {
        switch (filter) {
          case 'pinned':
            query = query.eq('is_sticky', true);
            break;
          case 'solved':
            query = query.eq('is_solved', true);
            break;
          case 'unsolved':
            query = query.eq('is_solved', false);
            break;
          case 'my-posts':
            if (userId) {
              query = query.eq('user_id', userId);
            }
            break;
          case 'my-replies':
            if (userId) {
              // This is more complex - we need to get discussions where the user has commented
              const { data: commentedDiscussionIds } = await supabase
                .from('forums_comments')
                .select('discussion_id')
                .eq('user_id', userId)
                .order('created_at', { ascending: false });
              
              if (commentedDiscussionIds && commentedDiscussionIds.length > 0) {
                const ids = [...new Set(commentedDiscussionIds.map(item => item.discussion_id))];
                query = query.in('id', ids);
              } else {
                // Return empty if user has no comments
                return { discussions: [], totalCount: 0 };
              }
            }
            break;
        }
      }

      // Apply sorting
      if (sort) {
        switch (sort) {
          case 'recent':
            query = query.order('last_activity_at', { ascending: false });
            break;
          case 'popular':
            // Popularity could be a combination of views and likes
            query = query.order('views', { ascending: false });
            break;
          case 'views':
            query = query.order('views', { ascending: false });
            break;
          case 'replies':
            // This is trickier and might require a different approach 
            // For now, we'll approximate by ordering by activity
            query = query.order('last_activity_at', { ascending: false });
            break;
          case 'likes':
            // This is also tricky without a count column
            // For now, we'll fall back to ordering by views
            query = query.order('views', { ascending: false });
            break;
        }
      } else {
        // Default sort by last activity
        query = query.order('last_activity_at', { ascending: false });
      }

      // Apply pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      // Execute the query
      const { data, error, count } = await query;

      if (error) throw error;

      // Transform the data to calculate counts and format dates
      const discussions = data?.map(discussion => {
        const result = {
          ...discussion,
          user: discussion.user,
          category: discussion.category,
          _count: {
            comments: discussion.comments ? discussion.comments.length : 0,
            likes: discussion.likes ? discussion.likes.length : 0
          },
          lastActivityFormatted: formatDistanceToNow(new Date(discussion.last_activity_at), { addSuffix: true })
        };
        
        // Clean up nested arrays that we don't need to return
        delete result.comments;
        delete result.likes;
        
        return result;
      }) || [];

      return { 
        discussions, 
        totalCount: count || 0 
      };
    }
  });
};

// Get popular discussions (most viewed)
export const usePopularDiscussions = (limit = 5) => {
  return useQuery({
    queryKey: ['popularDiscussions', limit],
    queryFn: async (): Promise<DiscussionWithRelations[]> => {
      const { data, error } = await supabase
        .from('forums_discussions')
        .select(`
          *,
          user:user_id (id, username, experience_level, avatar_url),
          category:category_id (id, slug, name, icon),
          comments:forums_comments (id),
          likes:forums_likes (id)
        `)
        .order('views', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Transform the data to calculate counts and format dates
      return data?.map(discussion => {
        const result = {
          ...discussion,
          user: discussion.user,
          category: discussion.category,
          _count: {
            comments: discussion.comments ? discussion.comments.length : 0,
            likes: discussion.likes ? discussion.likes.length : 0
          },
          lastActivityFormatted: formatDistanceToNow(new Date(discussion.last_activity_at), { addSuffix: true })
        };
        
        // Clean up nested arrays
        delete result.comments;
        delete result.likes;
        
        return result;
      }) || [];
    }
  });
};

// Get a single discussion with comments
export const useDiscussionDetail = (discussionId: string | null) => {
  const queryClient = useQueryClient();
  const session = queryClient.getQueryData<any>(['session']);
  const userId = session?.user?.id;

  return useQuery({
    queryKey: ['discussion', discussionId],
    queryFn: async (): Promise<DiscussionDetail | null> => {
      if (!discussionId) return null;

      // First, increment the view count
      await supabase.rpc('increment_discussion_view', { discussion_id: discussionId });

      // Fetch the discussion with all related data
      const { data: discussion, error: discussionError } = await supabase
        .from('forums_discussions')
        .select(`
          *,
          user:profiles!forums_discussions_user_id_fkey (id, username, experience_level, avatar_url),
          category:category_id (id, slug, name, icon),
          likes:forums_likes (id, user_id)
        `)
        .eq('id', discussionId)
        .single();

      if (discussionError) throw discussionError;
      if (!discussion) return null;

      // Fetch comments for the discussion
      const { data: comments, error: commentsError } = await supabase
        .from('forums_comments')
        .select(`
          *,
          user:user_id (id, username, experience_level, avatar_url),
          likes:forums_likes (id, user_id)
        `)
        .eq('discussion_id', discussionId)
        .order('created_at', { ascending: true });

      if (commentsError) throw commentsError;

      // Check if user has bookmarked this discussion
      let isBookmarked = false;
      if (userId) {
        const { data: bookmark } = await supabase
          .from('forums_bookmarks')
          .select('id')
          .eq('user_id', userId)
          .eq('discussion_id', discussionId)
          .maybeSingle();
        
        isBookmarked = !!bookmark;
      }

      // Transform comments to include like count and format date
      const transformedComments: CommentWithRelations[] = comments.map(comment => {
        const isLiked = userId ? comment.likes.some(like => like.user_id === userId) : false;
        
        return {
          ...comment,
          user: comment.user,
          _count: {
            likes: comment.likes ? comment.likes.length : 0
          },
          is_liked: isLiked,
          createdAtFormatted: formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })
        };
      });

      // Determine if the user has liked the discussion
      const isLiked = userId 
        ? discussion.likes.some((like: any) => like.user_id === userId)
        : false;

      // Prepare the final result
      const result: DiscussionDetail = {
        ...discussion,
        user: discussion.user,
        category: discussion.category,
        comments: transformedComments,
        _count: {
          comments: transformedComments.length,
          likes: discussion.likes ? discussion.likes.length : 0
        },
        is_bookmarked: isBookmarked,
        is_liked: isLiked,
        lastActivityFormatted: formatDistanceToNow(new Date(discussion.last_activity_at), { addSuffix: true }),
        createdAtFormatted: formatDistanceToNow(new Date(discussion.created_at), { addSuffix: true })
      };
      
      return result;
    },
    enabled: !!discussionId
  });
};

export const errNoUsername = new Error('You must have a username selected');

// Mutation to create a new discussion
export const useCreateDiscussion = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      title, 
      content, 
      categoryId 
    }: { 
      title: string; 
      content: string; 
      categoryId: string 
    }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to create a discussion');
      }

      const { data:p } = await supabase.from('profiles').select('username').eq('id', user?.id ?? '').single();

      if (!p || !p.username || p.username?.trim() === "") {
        throw errNoUsername;
      }

      const { data, error } = await supabase
        .from('forums_discussions')
        .insert({
          title,
          content,
          category_id: categoryId,
          user_id: session.session.user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate discussions queries to trigger a refetch
      queryClient.invalidateQueries({ queryKey: ['discussions'] });
      queryClient.invalidateQueries({ queryKey: ['popularDiscussions'] });
    }
  });
};

// Mutation to add a comment to a discussion
export const useAddComment = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      content, 
      discussionId, 
      parentCommentId 
    }: { 
      content: string; 
      discussionId: string; 
      parentCommentId?: string 
    }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to comment');
      }

      const { data:p } = await supabase.from('profiles').select('username').eq('id', user?.id ?? '').single();

      if (!p || !p.username || p.username?.trim() === "") {
        throw errNoUsername;
      }

      const { data, error } = await supabase
        .from('forums_comments')
        .insert({
          content,
          discussion_id: discussionId,
          user_id: session.session.user.id,
          parent_comment_id: parentCommentId || null
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate the specific discussion query to trigger a refetch
      queryClient.invalidateQueries({ queryKey: ['discussion', variables.discussionId] });
      // Also invalidate discussions to update counts
      queryClient.invalidateQueries({ queryKey: ['discussions'] });
    }
  });
};

// Mutation to toggle like on a discussion or comment
export const useToggleLike = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      type, 
      id 
    }: { 
      type: 'Discussion' | 'Comment'; 
      id: string 
    }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to like');
      }

      const userId = session.session.user.id;

      // Check if the user has already liked this item
      const { data: existingLike } = await supabase
        .from('forums_likes')
        .select('id')
        .eq('user_id', userId)
        .eq('likeable_type', type)
        .eq(type === 'Discussion' ? 'discussion_id' : 'comment_id', id)
        .maybeSingle();

      if (existingLike) {
        // Unlike: remove the existing like
        const { error } = await supabase
          .from('forums_likes')
          .delete()
          .eq('id', existingLike.id);

        if (error) throw error;
        return { action: 'unliked' };
      } else {
        // Like: add a new like
        const { error } = await supabase
          .from('forums_likes')
          .insert({
            user_id: userId,
            likeable_type: type,
            [type === 'Discussion' ? 'discussion_id' : 'comment_id']: id
          });

        if (error) throw error;
        return { action: 'liked' };
      }
    },
    onSuccess: (_, variables) => {
      // Determine which queries to invalidate based on what was liked
      if (variables.type === 'Discussion') {
        queryClient.invalidateQueries({ queryKey: ['discussion', variables.id] });
      } else {
        // For comments, we need to invalidate the parent discussion
        // This requires knowing the discussion ID, which we don't have here
        // A more sophisticated approach would be to pass the discussion ID as well
        queryClient.invalidateQueries({ queryKey: ['discussion'] });
      }
      // Also invalidate discussions list to update like counts
      queryClient.invalidateQueries({ queryKey: ['discussions'] });
    }
  });
};

// Mutation to toggle bookmark on a discussion
export const useToggleBookmark = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      discussionId 
    }: { 
      discussionId: string 
    }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to bookmark');
      }

      const userId = session.session.user.id;

      // Check if the user has already bookmarked this discussion
      const { data: existingBookmark } = await supabase
        .from('forums_bookmarks')
        .select('id')
        .eq('user_id', userId)
        .eq('discussion_id', discussionId)
        .maybeSingle();

      if (existingBookmark) {
        // Remove bookmark
        const { error } = await supabase
          .from('forums_bookmarks')
          .delete()
          .eq('id', existingBookmark.id);

        if (error) throw error;
        return { action: 'unbookmarked' };
      } else {
        // Add bookmark
        const { error } = await supabase
          .from('forums_bookmarks')
          .insert({
            user_id: userId,
            discussion_id: discussionId
          });

        if (error) throw error;
        return { action: 'bookmarked' };
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate the specific discussion query
      queryClient.invalidateQueries({ queryKey: ['discussion', variables.discussionId] });
    }
  });
};

interface ReportData {
  userId: string;
  type: 'Discussion' | 'Comment';
  itemId: string;
  reason: string;
  additionalComments?: string;
}

export function useReportMutation() {
  return useMutation({
    mutationFn: async ({
      userId,
      type,
      itemId,
      reason,
      additionalComments
    }: ReportData) => {
      if (!reason) {
        throw new Error("Please select a reason for reporting");
      }
      
      const { error } = await supabase
        .from('forums_reports')
        .insert({
          user_id: userId,
          ...(type === 'Discussion' ? { discussion_id: itemId } : { comment_id: itemId }),
          reason,
          additional_comments: additionalComments || null
        });
        
      if (error) throw error;
      return true;
    }
  });
}

// Function to submit a report (can be used outside React components)
export const submitReport = async (
  supabase: any,
  userId: string,
  type: 'Discussion' | 'Comment',
  itemId: string,
  reason: string,
  additionalComments?: string
) => {
  return supabase
    .from('forums_reports')
    .insert({
      user_id: userId,
      ...(type === 'Discussion' ? { discussion_id: itemId } : { comment_id: itemId }),
      reason,
      additional_comments: additionalComments || null
    });
};

export const useUpdateDiscussion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      data 
    }: { 
      id: string; 
      data: Partial<Discussion>; 
    }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to update a discussion');
      }

      // Update the discussion
      const { data: updatedDiscussion, error: updateError } = await supabase
        .from('forums_discussions')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;
      return updatedDiscussion;
    },
    onSuccess: (_, variables) => {
      // Invalidate specific discussion query
      queryClient.invalidateQueries({ queryKey: ['discussion', variables.id] });
      // Also invalidate discussions list to update UI
      queryClient.invalidateQueries({ queryKey: ['discussions'] });
    }
  });
};
